
import React, { useEffect, useRef } from "react";
import "../../styles/themes/elf-letter-bot.css";
import { normalizeInternalContentLinks } from "../../../shared/blog-content.js";
import { repairHtmlText } from "@/utils/displayText";

interface PostContentProps {
  content: string;
  theme?: string;
}

const PostContent = ({ content, theme }: PostContentProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const normalizedContent = repairHtmlText(normalizeInternalContentLinks(content));

  useEffect(() => {
    if (!contentRef.current) return;

    // Patch DOMContentLoaded so late-added listeners fire immediately
    const origAddEventListener = document.addEventListener.bind(document);
    document.addEventListener = function (
      type: string,
      listener: EventListenerOrEventListenerObject,
      options?: boolean | AddEventListenerOptions,
    ) {
      if (type === "DOMContentLoaded" && typeof listener === "function") {
        listener(new Event("DOMContentLoaded"));
        return;
      }
      origAddEventListener(type, listener, options);
    } as typeof document.addEventListener;

    const scripts = contentRef.current.querySelectorAll("script");
    scripts.forEach((orig) => {
      const replacement = document.createElement("script");
      Array.from(orig.attributes).forEach((attr) =>
        replacement.setAttribute(attr.name, attr.value),
      );
      replacement.textContent = orig.textContent;
      orig.parentNode?.replaceChild(replacement, orig);
    });

    document.addEventListener = origAddEventListener;
  }, [normalizedContent]);

  const isElfTheme = theme === "elf-letter-bot";

  const containerClasses = isElfTheme
    ? "theme-elf-letter-bot w-full"
    : "prose prose-lg max-w-3xl mx-auto px-4 mb-12";

  const contentClasses = isElfTheme ? "themed-blog-content" : "";

  return (
    <div className={containerClasses}>
      <div
        ref={contentRef}
        className={contentClasses}
        dangerouslySetInnerHTML={{ __html: normalizedContent }}
      />
    </div>
  );
};

export default PostContent;
